import { useState, useCallback, useRef, useEffect } from 'react';
import { generateId } from './floorPlanUtils';

const INITIAL_SETTINGS = {
  unit: 'imperial',
  gridSize: 12, // 1 foot
  snapToGrid: true,
  showDimensions: true,
  showGrid: true,
};

const INITIAL_STATE = {
  settings: { ...INITIAL_SETTINGS },
  walls: [],
  openings: [],
  furniture: [],
  rooms: [],
  viewport: { x: 0, y: 0, zoom: 1.0 },
};

const MAX_HISTORY = 50;

export default function useFloorPlanState(savedData, onSave) {
  const [state, setState] = useState(() => {
    if (savedData && savedData.walls) {
      return {
        ...INITIAL_STATE,
        ...savedData,
        settings: { ...INITIAL_SETTINGS, ...savedData.settings },
      };
    }
    return { ...INITIAL_STATE };
  });

  const [selectedId, setSelectedId] = useState(null);
  const historyRef = useRef([JSON.stringify(INITIAL_STATE)]);
  const historyIndexRef = useRef(0);
  const saveTimerRef = useRef(null);

  // Initialize history from saved data
  useEffect(() => {
    if (savedData && savedData.walls) {
      const initial = {
        ...INITIAL_STATE,
        ...savedData,
        settings: { ...INITIAL_SETTINGS, ...savedData.settings },
      };
      historyRef.current = [JSON.stringify(initial)];
      historyIndexRef.current = 0;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save with debounce
  useEffect(() => {
    if (!onSave) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      onSave(state);
    }, 1500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [state, onSave]);

  const pushHistory = useCallback((newState) => {
    const serialized = JSON.stringify(newState);
    const trimmed = historyRef.current.slice(0, historyIndexRef.current + 1);
    if (trimmed.length >= MAX_HISTORY) trimmed.shift();
    trimmed.push(serialized);
    historyRef.current = trimmed;
    historyIndexRef.current = trimmed.length - 1;
  }, []);

  const updateState = useCallback((updater) => {
    setState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      pushHistory(next);
      return next;
    });
  }, [pushHistory]);

  const undo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      const prev = JSON.parse(historyRef.current[historyIndexRef.current]);
      setState(prev);
    }
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current += 1;
      const next = JSON.parse(historyRef.current[historyIndexRef.current]);
      setState(next);
    }
  }, []);

  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  // --- Wall operations ---

  const addWall = useCallback((x1, y1, x2, y2) => {
    const wall = {
      id: generateId('wall'),
      x1, y1, x2, y2,
      thickness: 6,
    };
    updateState(prev => ({
      ...prev,
      walls: [...prev.walls, wall],
    }));
    return wall;
  }, [updateState]);

  const updateWall = useCallback((wallId, updates) => {
    updateState(prev => ({
      ...prev,
      walls: prev.walls.map(w => w.id === wallId ? { ...w, ...updates } : w),
    }));
  }, [updateState]);

  const deleteWall = useCallback((wallId) => {
    updateState(prev => ({
      ...prev,
      walls: prev.walls.filter(w => w.id !== wallId),
      openings: prev.openings.filter(o => o.wallId !== wallId),
    }));
    setSelectedId(null);
  }, [updateState]);

  // --- Opening operations ---

  const addOpening = useCallback((type, wallId, position, width = 36) => {
    const opening = {
      id: generateId('opening'),
      type,
      wallId,
      position,
      width,
      direction: type === 'door' ? 'left' : null,
      swing: type === 'door' ? 'out' : null,
    };
    updateState(prev => ({
      ...prev,
      openings: [...prev.openings, opening],
    }));
    return opening;
  }, [updateState]);

  const updateOpening = useCallback((openingId, updates) => {
    updateState(prev => ({
      ...prev,
      openings: prev.openings.map(o => o.id === openingId ? { ...o, ...updates } : o),
    }));
  }, [updateState]);

  const deleteOpening = useCallback((openingId) => {
    updateState(prev => ({
      ...prev,
      openings: prev.openings.filter(o => o.id !== openingId),
    }));
    setSelectedId(null);
  }, [updateState]);

  // --- Furniture operations ---

  const addFurniture = useCallback((assetId, x, y, assetData) => {
    const item = {
      id: generateId('furn'),
      assetId,
      x, y,
      rotation: 0,
      width: assetData.width,
      depth: assetData.depth,
      label: assetData.name,
      color: assetData.color,
    };
    updateState(prev => ({
      ...prev,
      furniture: [...prev.furniture, item],
    }));
    return item;
  }, [updateState]);

  const updateFurniture = useCallback((furnId, updates) => {
    updateState(prev => ({
      ...prev,
      furniture: prev.furniture.map(f => f.id === furnId ? { ...f, ...updates } : f),
    }));
  }, [updateState]);

  const deleteFurniture = useCallback((furnId) => {
    updateState(prev => ({
      ...prev,
      furniture: prev.furniture.filter(f => f.id !== furnId),
    }));
    setSelectedId(null);
  }, [updateState]);

  // --- Room operations ---

  const addRoom = useCallback((label, wallIds, color = '#E8F0FE') => {
    const room = {
      id: generateId('room'),
      label,
      wallIds,
      color,
    };
    updateState(prev => ({
      ...prev,
      rooms: [...prev.rooms, room],
    }));
    return room;
  }, [updateState]);

  const updateRoom = useCallback((roomId, updates) => {
    updateState(prev => ({
      ...prev,
      rooms: prev.rooms.map(r => r.id === roomId ? { ...r, ...updates } : r),
    }));
  }, [updateState]);

  const deleteRoom = useCallback((roomId) => {
    updateState(prev => ({
      ...prev,
      rooms: prev.rooms.filter(r => r.id !== roomId),
    }));
    setSelectedId(null);
  }, [updateState]);

  // --- Settings ---

  const updateSettings = useCallback((updates) => {
    updateState(prev => ({
      ...prev,
      settings: { ...prev.settings, ...updates },
    }));
  }, [updateState]);

  // --- Viewport (no history push) ---

  const updateViewport = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      viewport: { ...prev.viewport, ...updates },
    }));
  }, []);

  // --- Selection ---

  const getSelectedElement = useCallback(() => {
    if (!selectedId) return null;
    const wall = state.walls.find(w => w.id === selectedId);
    if (wall) return { type: 'wall', element: wall };
    const opening = state.openings.find(o => o.id === selectedId);
    if (opening) return { type: 'opening', element: opening };
    const furn = state.furniture.find(f => f.id === selectedId);
    if (furn) return { type: 'furniture', element: furn };
    const room = state.rooms.find(r => r.id === selectedId);
    if (room) return { type: 'room', element: room };
    return null;
  }, [selectedId, state]);

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    const sel = getSelectedElement();
    if (!sel) return;
    switch (sel.type) {
      case 'wall': deleteWall(selectedId); break;
      case 'opening': deleteOpening(selectedId); break;
      case 'furniture': deleteFurniture(selectedId); break;
      case 'room': deleteRoom(selectedId); break;
      default: break;
    }
  }, [selectedId, getSelectedElement, deleteWall, deleteOpening, deleteFurniture, deleteRoom]);

  return {
    state,
    selectedId,
    setSelectedId,
    getSelectedElement,
    deleteSelected,
    // Wall ops
    addWall,
    updateWall,
    deleteWall,
    // Opening ops
    addOpening,
    updateOpening,
    deleteOpening,
    // Furniture ops
    addFurniture,
    updateFurniture,
    deleteFurniture,
    // Room ops
    addRoom,
    updateRoom,
    deleteRoom,
    // Settings
    updateSettings,
    // Viewport
    updateViewport,
    // History
    undo,
    redo,
    canUndo,
    canRedo,
  };
}
